const preloadRoutes = () => {
    import('@/pages/About.vue')
    import('@/pages/Privacy.vue')
    import('@/pages/TOS.vue')
    import('@/pages/g25.vue')
    import('@/pages/formalstats.vue')
    import('@/pages/admixCalc.vue')
    import('@/pages/community.vue')
    import('@/pages/iframe.vue')
    import('@/pages/shares.vue')
    import('@/pages/labs.vue')
    import('@/pages/pca.vue')
    import('@/pages/health.vue')
    import('@/pages/snpbrowser.vue')
}
const preloadAllPages = () => {
    Object.values(preloadRoutes).forEach(loader => loader())
}
import { createCanAccess } from './entitlements'
import worker from '@/web-worker/main';
import {
    createApp,
    createSSRApp,
    defineAsyncComponent,
    nextTick,
    h
} from 'vue'
const start = Date.now()
import genoVer from './genoVersion.json'
import localforage from 'localforage'
localforage.config({
    driver: localforage.INDEXEDDB,
    name: 'localforage',
    storeName: 'genoplot',
});

// markdown-table — lazy loaded (only used on data pages)
let markdownTable
import('markdown-table').then(m => {
    markdownTable = m.markdownTable
})

import {
    get,
    set,
    keys,
    setMany,
    del,
    delMany,
    getMany
} from '@/plugins/idb-keyval';

import {
    compressToUTF16,
    decompressFromUTF16
} from "async-lz-string";
window.compressToUTF16 = compressToUTF16;
window.decompressFromUTF16 = decompressFromUTF16;
let lz = {
    compress: compressToUTF16,
    decompress: decompressFromUTF16
}

import mitt from 'mitt'
// smoothscroll — defer polyfill (not critical for FrontPage)
import('smoothscroll-polyfill').then(m => m.default.polyfill())
// yamljs, retry — lazy loaded (only used on data pages)
let yamljs, retry
import('yamljs').then(m => {
    yamljs = m.default
})
import('retry').then(m => {
    retry = m.default
})
import {
    isMobile
} from 'mobile-device-detect';

import dbCache from '@/plugins/indexDBCache.js';
import {
    setAppInstance,
    dbCacheFunctions
} from '@/plugins/indexDBCache.js';

import {
    store
} from "./Store"
store.commit('change', {
    contacted: false
})

// Dev/staging entitlement seed for the SNP browser feature.
//
// Server-side ENTITLEMENTS for `snpbrowser` will be granted per
// account once the backend ships. While the feature is being built,
// dev/staging environments need access without server config — this
// seeds + re-merges the rule whenever ENTITLEMENTS is set so the
// route resolves locally. Production users only see the page when
// the rule is granted server-side.
const SNP_BROWSER_DEV_RULE = {
    'in': [{ 'var': 'env' }, ['development', 'staging']]
}
function ensureSnpBrowserDevEntitlement() {
    const env = store.state.env
    if (env !== 'development' && env !== 'staging') return
    const current = store.state.ENTITLEMENTS || {}
    if (current.snpbrowser) return
    store.commit('change', {
        ENTITLEMENTS: { ...current, snpbrowser: SNP_BROWSER_DEV_RULE }
    })
    if (!store.state.SET_ENTITLEMENTS_READY) {
        store.commit('change', { SET_ENTITLEMENTS_READY: true })
    }
}
ensureSnpBrowserDevEntitlement()
store.subscribe((mutation, state) => {
    if (mutation.type !== 'change' || !mutation.payload) return
    if ('ENTITLEMENTS' in mutation.payload) {
        // Server load just replaced ENTITLEMENTS — re-merge our dev rule.
        if ((state.env === 'development' || state.env === 'staging') &&
            state.ENTITLEMENTS && !state.ENTITLEMENTS.snpbrowser) {
            const merged = { ...state.ENTITLEMENTS, snpbrowser: SNP_BROWSER_DEV_RULE }
            // Direct assignment to avoid a recursive `change` subscription loop.
            state.ENTITLEMENTS = merged
        }
    }
})
// import gitinfo from './git.js' // generated at build time
import hexUtils from './plugins/hexutils'

import router from "./router";
import {
    setRouterGlobals
} from "./router";
import RouteTopProgress from './plugins/route-top-progress'

// intro.js — lazy loaded, only needed for onboarding tours
let introjs = null
const introjsReady = import('intro.js/intro.js').then(m => {
    introjs = m.default()
    introjs.setOption("overlayOpacity", 0.5)
    introjs.setOption("keyboardNavigation", false)
    introjs.setOption("exitOnOverlayClick", false)
    introjs.setOption("scrollToElement", false)
    introjs.setOption("scrollTo", "tooltip")
    introjs.setOption("hidePrev", false)
    introjs.setOption("hideNext", false)
    introjs.setOption("skipLabel", "Cancel")
    // Load intro.js CSS alongside the JS
    import("@/style/introjs.min.css")
    import("@/style/introjs-nassim.css")
    return introjs
})

import useStepAudioContext from '@/plugins/useStepAudioContext';
// Howler — truly lazy, only loads when first accessed
let Howl, Howler
let _howlerLoading

function loadHowler() {
    if (!_howlerLoading) {
        _howlerLoading = import('howler').then(m => {
            Howl = m.Howl;
            Howler = m.Howler
        })
    }
    return _howlerLoading
}

import "./registerServiceWorker";
import GenoLogo from './components/GenoLogo.vue';
import GenoSpinner from './components/GenoSpinner.vue'

import AmplifyEventBus from './plugins/aws-amplify-vue/events/AmplifyEventBus';

function smartLog(...args) {
    Function.prototype.apply.call(console.log, console, args);
    const error = args.find(arg =>
        arg instanceof Error || (arg && typeof arg === 'object' && 'message' in arg && 'stack' in arg)
    );
    if (error && window.logDebug) {
        AmplifyEventBus.emit('appEvents', {
            exec: 'trackActivity',
            component: 'nav',
            params: {
                recovery: error.message,
                stack: error.stack,
                function: 'consoleError',
                level: 'ERROR',
                timestamp: new Date().toISOString(),
                source: 'smartLog',
            }
        });
    }
};
window.smartLog = smartLog;

// Mobile resume safety net
document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState === 'visible') {
        if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
            const reg = await navigator.serviceWorker.getRegistration()
            if (reg && reg.waiting) {
                reg.waiting.postMessage({
                    type: 'SKIP_WAITING'
                })
            }
        }
    }
})

import App from "./App.vue";

//plugins
import GenoUI from './plugins/default';

import aws_exports from './aws-exports';
import VModal from './components/vModal';
import { px } from '@/utils/uiScale'
import AbbreviatedNumber from './components/abbreviate';

// AWS Amplify + Apollo — deferred until after mount (not needed for FrontPage render)
let Amplify, Auth, I18n, Logger, Hub
let defaultClient, appsyncURL
let awsReady

function initAWS() {
    if (awsReady) return awsReady
    awsReady = import('aws-amplify').then(async (aws) => {
        Amplify = aws.Amplify;
        Auth = aws.Auth;
        I18n = aws.I18n;
        Logger = aws.Logger;
        Hub = aws.Hub

        Amplify.configure(aws_exports)
        Auth.configure({
            storage: window.localStorage
        })

        // Hub auth listener
        Hub.listen('auth', (data) => {
            const {
                payload
            } = data;
            if (payload.event === "cognitoHostedUI") {
                smartLog('User signed in via Social Provider');
                AmplifyEventBus.emit('authState', 'socialSignIn')
            } else if (payload.event === 'signIn_failure' && payload.message == 'The OAuth response flow failed') {
                let message = payload.data && payload.data.message ? payload.data.message : 'Sign In Failed!';
                if (message == 'User+is+not+enabled') {
                    smartLog('hub event', message)
                    let params = {
                        className: 'toastDiaabledUser toastError',
                        message: 'Your account has been diaabled as a precaution<br>due to unusual activity on your account.<br><br>Please contact us if you feel this action was erroneous.<br>',
                        toast: true,
                        position: 'top-center',
                        timestamp: Date.now(),
                    }
                    let sendEvent = {
                        exec: 'setError',
                        type: 'appEvents',
                        component: 'signInModal',
                        timestamp: Date.now(),
                        params: 'Account is disabled'
                    }
                    localStorage.setItem('lastToast', JSON.stringify(params))
                    localStorage.setItem('lastEvent', JSON.stringify(sendEvent))
                    AmplifyEventBus.emit('appEvents', {
                        exec: 'showToast',
                        component: 'nav',
                        params: params
                    })
                    AmplifyEventBus.emit('appEvents', {
                        exec: 'setError',
                        component: 'signInModal',
                        params: 'Account is disabled'
                    });
                } else {
                    AmplifyEventBus.emit('appEvents', {
                        exec: 'setError',
                        component: 'signInModal',
                        params: message
                    });
                }
            }
        });

        // Apollo  GraphQL
        //
        const OrigWS = window.WebSocket
        window.WebSocket = function(url, protocols) {
            const ws = new OrigWS(url, protocols)
            if (url.includes('appsync-realtime')) {
                ws.addEventListener('message', e => {
                    try {
                        const msg = JSON.parse(e.data)
                        if (msg.type === 'connection_ack') console.log('[AppSync] connection_ack', msg.payload)
                        if (msg.type === 'data') console.log('[AppSync] data id=', msg.id, msg.payload?.data)
                    } catch {}
                })
            }
            return ws
        }
        window.WebSocket.prototype = OrigWS.prototype

        const appsync = await import('@/appsync')
        defaultClient = appsync.default // defaultClient is now the default export
        appsyncURL = appsync.appsyncURL

        return {
            Amplify,
            Auth,
            I18n,
            Logger,
            Hub,
            defaultClient,
            appsyncURL
        }
    })
    return awsReady
}
// Heavy components — lazy loaded (not needed on FrontPage)
const Multiselect = defineAsyncComponent(() => import('@/components/multiselect'))
const SwiperComponent = defineAsyncComponent(() => import('@/components/swiper/swiper.vue'))
const SlideComponent = defineAsyncComponent(() => import('@/components/swiper/slide.vue'))
const EChartsBase = defineAsyncComponent(() => import('vue-echarts'))

// Wrap vue-echarts to defer initial setOption to the next macrotask,
// matching DonutResultsECharts' nextTick+setTimeout(0) pattern.
// Without this, vue-echarts calls init+setOption in the same nextTick
// and ECharts skips the entrance animation.
const ECharts = {
    name: 'VChartAnimated',
    inheritAttrs: false,
    data() {
        return {
            ready: false
        }
    },
    mounted() {
        this.$nextTick(() => {
            setTimeout(() => {
                this.ready = true
            }, 0)
        })
    },
    render() {
        const attrs = {
            ...this.$attrs
        }
        if (!this.ready) {
            // Pass empty option on first render so echarts.init creates an empty chart
            attrs.option = {}
        }
        // When ready flips to true, the real option flows through,
        // and vue-echarts calls setOption (empty→data), triggering animation
        return h(EChartsBase, attrs, this.$slots)
    }
}
const VueNumberInput = defineAsyncComponent(() => import('@chenfengyuan/vue-number-input'))

// Element Plus — lazy loaded (not needed on FrontPage)
// CSS deferred until first component is actually rendered
let _elPlusCSSLoaded = false

// LZString — truly lazy (only loads on first access, not during page load)
let LZString
let _lzLoading

function loadLZString() {
    if (!_lzLoading) _lzLoading = import('lz-string').then(m => {
        LZString = m.default
    })
    return _lzLoading
}

import FloatingVue from 'floating-vue'
import 'floating-vue/dist/style.css'
import '@/style/tooltip-compat.css'
import '@/style/element-icons-compat.css'
import '@/assets/amplify.css'
import '@/assets/ag.css'
import CappedArrayPlugin from '@/plugins/cappedArrays';
// juice — lazy loaded
import('juice/client').then(m => {
    window.juice = m.default || m
})
// zlib — lazy loaded (only used on data pages)
let zlib
import('zlib').then(m => {
    zlib = m.default || m
})

// ---------- Create the Vue 3 app ----------
// Use createSSRApp on the frontpage so Vue hydrates the pre-rendered HTML
// instead of replacing it. The inline script in index.html clears #app on
// non-frontpage routes, so _hasSSRContent is only true on frontpage.
const _appEl = document.getElementById('app')
const _hasSSRContent = _appEl && _appEl.innerHTML.trim().length > 0
const app = _hasSSRContent ? createSSRApp(App) : createApp(App)

// Suppress noisy floating-vue "classes" attr warning (cosmetic, tooltips work fine)
const origWarnHandler = app.config.warnHandler
app.config.warnHandler = (msg, vm, trace) => {
    if (msg.includes('Extraneous non-props attributes (classes)') && trace && trace.includes('VPopper')) return
    if (origWarnHandler) return origWarnHandler(msg, vm, trace)
    console.warn(`[Vue warn]: ${msg}${trace}`)
}

// Register store and router
app.use(store)
app.use(router)

// Element Plus components — lazy loaded (not needed on FrontPage)
// CSS loaded from public/ on first use — avoids Vite extracting it as render-blocking <link>
const elPlus = () => {
    if (!_elPlusCSSLoaded) {
        _elPlusCSSLoaded = true
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = '/css/element-plus.css'
        document.head.appendChild(link)
    }
    return import('element-plus')
}
app.component('ElTree', defineAsyncComponent(() => elPlus().then(m => m.ElTree)))
app.component('ElProgress', defineAsyncComponent(() => elPlus().then(m => m.ElProgress)))
// ElOption loaded eagerly with ElSelect — async ElOption causes empty dropdowns
// because the select renders before its child options resolve
app.component('ElOption', defineAsyncComponent(() => elPlus().then(m => m.ElOption)))
app.component('ElTabs', defineAsyncComponent(() => elPlus().then(m => m.ElTabs)))
app.component('ElTabPane', defineAsyncComponent(() => elPlus().then(m => m.ElTabPane)))
app.component('ElRadioButton', defineAsyncComponent(() => elPlus().then(m => m.ElRadioButton)))
app.component('ElDrawer', defineAsyncComponent(() => elPlus().then(m => m.ElDrawer)))
// Virtual scrollbar — consistent thumb across platforms/browsers (used by
// the file-share modal; falls back to native overflow on mobile).
app.component('ElScrollbar', defineAsyncComponent(() => elPlus().then(m => m.ElScrollbar)))
// ── Vue 3 v-on:change timing fix ──────────────────────────────────────
// In Vue 2, v-model updated before v-on:change fired. In Vue 3, change
// can fire before v-model updates, so handlers read stale values.
// wrapDeferChange() defers the change event to nextTick so v-model is
// current when the handler runs. Applied to all components that use
// v-on:change with v-model in templates. See: @/components/VueSliderCompat.js
// VueSlider itself is wrapped at import time in VueSliderCompat.js
// NOTE: vue-multiselect has a separate compat fix — it emits both
// 'update:modelValue' and 'input' so v-on:input handlers still work.
// See: @/components/multiselect/multiselectMixin.js (search for '$emit')
import {
    wrapDeferChange
} from '@/components/VueSliderCompat.js'
app.component('ElSwitch', defineAsyncComponent(() => elPlus().then(m => wrapDeferChange(m.ElSwitch, 'ElSwitch'))))
app.component('ElSelect', defineAsyncComponent(() => elPlus().then(m => wrapDeferChange(m.ElSelect, 'ElSelect'))))
app.component('ElRadio', defineAsyncComponent(() => elPlus().then(m => wrapDeferChange(m.ElRadio, 'ElRadio'))))
app.component('ElRadioGroup', defineAsyncComponent(() => elPlus().then(m => wrapDeferChange(m.ElRadioGroup, 'ElRadioGroup'))))

// Custom plugins
import GlobalMixins from '@/plugins/globalMixins'
app.use(GlobalMixins)
app.use(GenoUI)
app.use(CappedArrayPlugin)
app.use(useStepAudioContext)
app.use(VModal, {
    dialog: true,
    dynamic: true,
    defaultProps: {
        draggable: true,
    },
    injectModalsContainer: true
})

// Route top progress
app.use(RouteTopProgress, {
    router,
    height: 3,
    minDuration: 100,
    color: '#337ab7',
    // Above the share drawers (Element Plus stacks them past 200000 and
    // the g25/calc pages force 99999) and the share curtain: a top
    // progress strip is meant to show over whatever is loading behind
    // it, and at the default 9999 an open drawer hid it entirely.
    zIndex: 2147483100,
})

// Floating Vue (replaces v-tooltip)
const canHover = window.matchMedia('(hover: hover)').matches
const smallScreen = window.innerWidth < 760;
const isTouch = navigator.maxTouchPoints > 0;

if (!canHover || smallScreen) {
    // No-op tooltip directive for touch/small screens
    app.directive('tooltip', {
        mounted() {},
        updated() {},
        unmounted() {}
    });
} else {
    app.use(FloatingVue, {
        themes: {
            tooltip: {
                delay: {
                    show: 300,
                    hide: 100
                },
                distance: 5,
                html: true,
            }
        }
    });

    // Wrap the floating-vue tooltip directive to support the old v-tooltip
    // `classes` property (array) by converting it to `popperClass` (string),
    // and strip old string-format `offset` (floating-vue uses distance/skidding).
    const origTooltip = app._context.directives['tooltip']
    if (origTooltip) {
        const transformBinding = (binding) => {
            if (binding.value && typeof binding.value === 'object') {
                let val = binding.value
                let changed = false
                if (val.classes) {
                    const popperClass = Array.isArray(val.classes) ? val.classes.join(' ') : val.classes
                    const {
                        classes: _removed,
                        ...rest
                    } = val
                    val = {
                        ...rest,
                        popperClass
                    }
                    changed = true
                }
                if (typeof val.offset === 'string') {
                    const {
                        offset: _removed,
                        ...rest
                    } = val
                    val = rest
                    changed = true
                }
                if (changed) return {
                    ...binding,
                    value: val
                }
            }
            return binding
        }
        const origMounted = origTooltip.mounted
        const origUpdated = origTooltip.updated
        if (origMounted) {
            origTooltip.mounted = (el, binding, vnode, prevVnode) =>
                origMounted(el, transformBinding(binding), vnode, prevVnode)
        }
        if (origUpdated) {
            origTooltip.updated = (el, binding, vnode, prevVnode) =>
                origUpdated(el, transformBinding(binding), vnode, prevVnode)
        }
    }
}

// Global mixin for hex utils
app.mixin(hexUtils);

// Global components
app.component('multiselect', Multiselect)
app.component('geno-logo', GenoLogo)
app.component('geno-spinner', GenoSpinner)
app.component('AbbreviatedNumber', AbbreviatedNumber);
import TourButton from '@/components/TourButton.vue'
app.component('TourButton', TourButton);

// Swiper components (custom vue-awesome-swiper wrapper)
app.component('swiper', SwiperComponent)
app.component('swiper-slide', SlideComponent)

// vue-echarts
app.component('v-chart', ECharts)

// vue-number-input
app.component('number-input', VueNumberInput)

// ag-grid: no global registration — pages that use it (g25, admixCalc, selections)
// import AgGridVue locally and register ClientSideRowModelModule themselves.

// b-progress / b-progress-bar — native replacements for BootstrapVue (Vue 2 only)
import BProgress from '@/components/BProgress.vue'
import BProgressBar from '@/components/BProgressBar.vue'
app.component('b-progress', BProgress)
app.component('b-progress-bar', BProgressBar)

// Amplify auth components — lazy loaded (only needed when auth UI renders)
const amplifyAuth = () => import('./plugins/aws-amplify-vue/components/authenticator')
app.component('amplify-sign-in', defineAsyncComponent(() => amplifyAuth().then(m => m.SignIn)))
app.component('amplify-sign-up', defineAsyncComponent(() => amplifyAuth().then(m => m.SignUp)))
app.component('amplify-sign-out', defineAsyncComponent(() => amplifyAuth().then(m => m.SignOut)))
app.component('amplify-confirm-sign-up', defineAsyncComponent(() => amplifyAuth().then(m => m.ConfirmSignUp)))
app.component('amplify-confirm-sign-in', defineAsyncComponent(() => amplifyAuth().then(m => m.ConfirmSignIn)))
app.component('amplify-forgot-password', defineAsyncComponent(() => amplifyAuth().then(m => m.ForgotPassword)))
app.component('amplify-set-mfa', defineAsyncComponent(() => amplifyAuth().then(m => m.SetMfa)))
app.component('amplify-require-new-password', defineAsyncComponent(() => amplifyAuth().then(m => m.RequireNewPassword)))

// Stub components for uninstalled Vue 2 libraries
import VueContext from '@/components/VueContext.vue'
app.component('vue-context', VueContext)
// vue-form replacement (Vue 3 compatible)
import VueFormCompat from '@/components/vue-form-compat.js'
app.use(VueFormCompat)
// Reactivesearch-vue replacements (Vue 3 compatible, queries ES directly)
import ReactivesearchCompat from '@/components/reactivesearch-compat.js'
app.use(ReactivesearchCompat)
// vue-intercom (Vue 3 port — was Vue.use() in monitoring.js)
import VueIntercom from '@/plugins/vue-intercom'
app.use(VueIntercom, {
    appId: 'tbkyc5qe'
})
// vue-dropzone (Vue 3 wrapper around raw dropzone library, replaces vue2-dropzone)
import VueDropzone from '@/components/VueDropzone.vue'
app.component('vue-dropzone', VueDropzone)
// pulse-loader stub
app.component('pulse-loader', {
    render() {
        return h('span', '...')
    },
})

// Global properties (replaces Vue.prototype)
const gp = app.config.globalProperties
// $px(n): a design-time pixel size at the current UI scale, for component
// props that take pixels (el-table-column width, el-scrollbar height…).
// See src/utils/uiScale.js.
gp.$px = px
gp.$canAccess = () => false
gp.$genoVer = genoVer.version;
gp.$genoChanges = genoVer.changes
gp.$forage = localforage;
Object.defineProperty(gp, '$mdtable', {
    get: () => markdownTable,
    configurable: true
})
if (typeof(worker) != 'undefined') {
    gp.$worker = worker
}
gp.$indexedDB = {
    get,
    set,
    keys,
    setMany,
    del,
    delMany,
    getMany
}
gp.$lz = lz
Object.defineProperty(gp, '$yaml', {
    get: () => yamljs,
    configurable: true
})
Object.defineProperty(gp, '$retry', {
    get: () => retry,
    configurable: true
})
gp.$isMobile = isMobile;
gp.$dbCache = dbCache
gp.$eventBus = AmplifyEventBus;
Object.defineProperty(gp, '$zlib', {
    get: () => zlib,
    configurable: true
})
gp.$tempstate = {}
// AWS/Apollo — lazy getters (available after async load)
// Provide safe fallbacks so components that render before initAWS() resolves
// don't throw on $Amplify.I18n.get() or new $Amplify.Logger()
const _I18nFallback = {
    get: (key) => key
}
const _LoggerFallback = class {
    constructor() {}
    debug() {}
    info() {}
    warn() {}
    error() {}
}
Object.defineProperty(gp, '$Amplify', {
    get: () => ({
        Auth,
        I18n: I18n || _I18nFallback,
        Logger: Logger || _LoggerFallback
    }),
    configurable: true
})
Object.defineProperty(gp, '$auth', {
    get: () => Auth,
    configurable: true
})
// $apolloClient, $appsyncURL — set by awsReady after Apollo loads
// $apollo proxy: delegates to $apolloClient once available, so components
// can use this.$apollo.query/mutate/subscribe at any point.
const apolloProxy = new Proxy({}, {
    get(_, prop) {
        if (!gp.$apolloClient) {
            // Return a function that waits for the client then calls the method
            return (...args) => {
                return new Promise((resolve, reject) => {
                    const check = () => {
                        if (gp.$apolloClient) {
                            try {
                                resolve(gp.$apolloClient[prop](...args))
                            } catch (e) {
                                reject(e)
                            }
                        } else {
                            setTimeout(check, 50)
                        }
                    }
                    check()
                })
            }
        }
        const val = gp.$apolloClient[prop]
        return typeof val === 'function' ? val.bind(gp.$apolloClient) : val
    }
})
// Use defineProperty so the mixin-set $apollo takes precedence on new instances,
// but pre-mounted components fall back to this proxy via globalProperties
Object.defineProperty(gp, '$apollo', {
    get: () => apolloProxy,
    configurable: true
})
// $intro is lazy — proxy getter so it's available after async load
Object.defineProperty(gp, '$intro', {
    get: () => introjs,
    configurable: true
})
Object.defineProperty(gp, '$howl', {
    get: () => {
        loadHowler();
        return Howl
    },
    configurable: true
})
Object.defineProperty(gp, '$howler', {
    get: () => {
        loadHowler();
        return Howler
    },
    configurable: true
})

// Mitt-based $on/$off/$emit shim for Vue 2 $root event bus pattern
const rootEmitter = mitt()
gp.$on = rootEmitter.on.bind(rootEmitter)
gp.$off = rootEmitter.off.bind(rootEmitter)
gp.$emit = rootEmitter.emit.bind(rootEmitter)

// Google Analytics (vue-gtag-next for Vue 3)
import VueGtag, { trackRouter } from 'vue-gtag-next'
const _gaId = import.meta.env.VITE_GA_MEASUREMENT_ID
if (_gaId) {
    app.use(VueGtag, {
        property: {
            id: _gaId,
            params: {
                user_id: store.getters.user ? store.getters.user.username : null
            }
        }
    })
    trackRouter(router, {
        template(to) {
            return {
                page_title: to.name,
                page_path: to.path,
            }
        }
    })
} else {
    gp.$gtag = {
        event() {},
        customMap() {},
        set() {},
    }
}

// Toast notifications (vue-toasted replacement for Vue 3)
import VueToastedCompat from '@/plugins/toasted'
app.use(VueToastedCompat)

// Stub $Progress (route-top-progress may not expose this)
if (!gp.$Progress) {
    gp.$Progress = {
        start() {},
        finish() {},
        fail() {},
        set() {}
    }
}

// Stub $smoothScroll
if (!gp.$smoothScroll) {
    gp.$smoothScroll = (opts) => {
        const el = typeof opts?.scrollTo === 'string' ? document.querySelector(opts.scrollTo) : opts?.scrollTo
        if (el) el.scrollIntoView({
            behavior: 'smooth'
        })
    }
}

// Stub $genericevent (analytics/intercom)
if (!gp.$genericevent) {
    gp.$genericevent = {
        boot() {},
        shutdown() {},
        update() {},
        hide() {},
        show() {},
        showArticle() {},
        trackEvent() {},
    }
}

// Provide the app instance to indexDBCache for $opfs
setAppInstance(app)

// Provide router globals for router.js
// $auth uses a getter since Auth loads async
const routerGlobals = {
    $tempstate: gp.$tempstate,
    $eventBus: AmplifyEventBus,
}
Object.defineProperty(routerGlobals, '$auth', {
    get: () => Auth,
    configurable: true
})
setRouterGlobals(routerGlobals)

// Route-based AWS/Apollo loading strategy:
// Public routes: mount first, load AWS in background (fast FCP/LCP)
// Auth routes: load AWS before mount so $apollo is ready immediately
const publicPaths = [
    '/', '/home', '/signedout', '/demo',
    '/about', '/privacy', '/tos', '/contactus', '/help',
    '/shares', '/shared', '/faq', '/news', '/version', '/discussions'
]
const pathname = window.location.pathname
const isPublicRoute = publicPaths.some(p => pathname === p || pathname.startsWith(p + '/'))

function installApollo({
    defaultClient: dc,
    appsyncURL: url
}) {
    gp.$apolloClient = dc
    gp.$appsyncURL = url
    store.commit('change', {
        graphQLurl: aws_exports.aws_appsync_graphqlEndpoint
    })
        const canAccess = createCanAccess(store)
        gp.$canAccess = canAccess
}

if (isPublicRoute) {
    window.requestIdleCallback(() => {
        console.log('▶ Hydrating app')
        app.mount("#app")
        setTimeout(() => {
            initAWS().then(installApollo)
            let start = Date.now();
            console.log('starting defer cache', start)
            window.requestIdleCallback(() => {
                setTimeout(() => {
                    preloadRoutes()
                }, 2000)
                setTimeout(() => {
                    dbCacheFunctions.cacheAll();
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        window.__PRERENDER_READY__ = true
                      })
                    })
                }, 3000)
            })
        }, 1000)
    })
} else {
    // Auth routes: load AWS *and the auth UI stack* before mounting, so $apollo is
    // ready and the login modal can actually be shown the moment it is asked for.
    //
    // LoginModal (in App.vue) and the Amplify auth components are async chunks.
    // These imports used to be fire-and-forget, with the mount gated only on
    // initAWS — so whichever finished first decided the outcome. On a WARM load
    // the chunks are cached and win, and the modal appears; on a genuine FIRST
    // load they are still downloading when the router guard asks for the sign-in
    // prompt, `$modal.show('login')` no-ops against a component that has not
    // mounted, and the visitor lands on the home page with no prompt at all.
    // That was systemic across every requiresAuth route on a cold visit — most
    // visible on links sent to brand-new users (partner invites, share redeems,
    // referral /signup), because those people are first-time visitors by
    // definition and have nothing cached.
    //
    // Awaiting them costs nothing in the common case: they resolve inside the
    // slower, network-bound initAWS wait, which is awaited here regardless.
    // Public/marketing routes never reach this branch.
    // The two auth chunks are awaited so the login modal is mountable before the
    // guard asks for it — but they must never be able to BLOCK the mount. Without
    // the catch, a single failed chunk fetch rejects the whole Promise.all and the
    // app never mounts: a blank page instead of a working app that is merely
    // missing a modal. (Seen for real when Vite invalidated its dep cache and the
    // authenticator chunk 504'd.) Swallow their failures; only initAWS is load
    // bearing, and it was already the sole gate before.
    Promise.all([
        import('./components/modals/LoginModal.vue').catch(err => { console.error('LoginModal preload failed', err); return null }),
        import('./plugins/aws-amplify-vue/components/authenticator').catch(err => { console.error('authenticator preload failed', err); return null }),
        initAWS()
    ]).then(([, , result]) => {
        installApollo(result)
        app.mount("#app")
        dbCacheFunctions.cacheAll()
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        window.__PRERENDER_READY__ = true
                      })
                    })
    })
}

if (sessionStorage.redirect) {
    const redirect = sessionStorage.redirect
    delete sessionStorage.redirect
    router.push(redirect)
}

const ifObjectisJSON = function(obj) {
    return (typeof obj === 'object' && obj !== null && obj.constructor === Object) || Array.isArray(obj)
}

function CacheDB(namespace, prefix, config) {
    this.config = config || {
        auto: 0
    };
    this.config.auto = this.config.auto || 0
    this.namespace = namespace || "CacheDBDefaultNameSpace";
    this.prefix = prefix || "CacheDBDefaultPrefix";
    this.read = async function(key, config) {
        config = config || {};
        config.type = config.type || (this.config.auto ? "auto" : "text");
        return new Promise((resolve, reject) => {
            caches.open(this.namespace)
                .then(cache => {
                    cache.match(new Request(`https://${this.prefix}/${encodeURIComponent(key)}`))
                        .then(async response => {
                            if (config.type === 'auto') {
                                switch (response.headers.get('Content-Type')) {
                                    case "application/json":
                                        config.type = "json";
                                        break;
                                    case "application/octet-stream":
                                        config.type = "arrayBuffer";
                                        break;
                                    case "text/number":
                                        config.type = "number";
                                        break;
                                    case "text/boolean":
                                        config.type = "boolean";
                                        break;
                                    default:
                                        config.type = "text";
                                        break;
                                }
                            }
                            switch (config.type) {
                                case "number":
                                    resolve(Number(await response.text()));
                                    return;
                                case "json":
                                    resolve(response.json());
                                    return;
                                case "arrayBuffer":
                                    resolve(response.arrayBuffer());
                                    return;
                                case 'blob':
                                    resolve(response.blob());
                                    return;
                                case 'text':
                                    resolve(response.text());
                                    return;
                                case 'boolean':
                                    resolve(await response.text() == 1);
                                    return;
                                default:
                                    resolve(response.body);
                                    return;
                            }
                        })
                })
                .catch(err => {
                    console.error('CacheDB Read Erorr:' + err);
                    reject(null);
                });
        })
    }
    this.write = async function(key, value, config) {
        config = config || {};
        config.type = config.type || (this.config.auto ? "auto" : "text");
        if (config.type === 'auto')
            config.type = ifObjectisJSON(value) ? 'json' : typeof value;
        switch (config.type) {
            case "json":
                config.content_type = "application/json";
                value = JSON.stringify(value);
                break;
            case "arrayBuffer":
                config.content_type = "application/octet-stream";
                break;
            case 'blob':
                config.content_type = "application/octet-stream";
                break;
            case 'number':
                config.content_type = "text/number";
                value = value.toString();
                break;
            case 'boolean':
                config.content_type = "text/boolean";
                value = value ? 1 : 0
                break;
            default:
                break;
        }
        return new Promise((resolve, reject) => {
            caches.open(this.namespace).then(cache => {
                cache.put(
                    new Request(`https://${this.prefix}/${encodeURIComponent(key)}`),
                    new Response(value, {
                        headers: {
                            'Content-Type': config.content_type
                        }
                    })).then(resolve(1))
            }).catch(err => {
                console.error('CacheDB Write Erorr:' + err);
                reject(0)
            });
        })
    }
    this.delete = async function(key) {
        return new Promise((resolve, reject) => {
            caches.open(this.namespace).then(cache => {
                    cache.delete(new Request(`https://${this.prefix}/${encodeURIComponent(key)}`))
                        .then(resolve(true))
                })
                .catch(err => {
                    console.error('CacheDB Delete Erorr:' + err);
                    reject(false)
                });
        })
    }
    this.list = async function() {
        return new Promise((resolve, reject) => {
            caches.open(this.namespace).then(cache => {
                cache.keys().then(keys => {
                    resolve(keys.map(key => decodeURIComponent(key.url.split('/').pop())));
                })
            }).catch(err => {
                console.error('CacheDB List Erorr:' + err);
                reject([])
            });
        })
    }
    this.all = async function() {
        const data = {};
        return new Promise(async (resolve, reject) => {
            this.list().then(async keys => {
                await Promise.all(keys.map(async key => {
                    data[key] = await this.read(key);
                }))
                resolve(data);
            }).catch(err => {
                console.error('CacheDB All Erorr:' + err);
                reject([])
            });
        })
    }
    this.clear = async function() {
        return new Promise((resolve, reject) => {
            this.list().then(keys => {
                keys.forEach(key => {
                    this.delete(key)
                })
                resolve(true);
            }).catch(err => {
                console.error('CacheDB Clear Erorr:' + err);
                reject(false)
            });
        })
    }
    this.destroy = async function() {
        return new Promise((resolve, reject) => {
            this.clear().then(() => {
                caches.delete(this.namespace).then(resolve(true));
            }).catch(err => {
                console.error('CacheDB Destroy Erorr:' + err);
                reject(false)
            });
        })
    }
}
